import { Field, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { PortionSievingDetailsSection, PortionSievingEquipmentSection, PortionSievingRecordGridSection } from '.';

// This section is copied from the ASTM C136 worksheet
let PortionSievingPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, namePrefix }) => {
  const isWholePortion = namePrefix!.endsWith('WholePortion');
  const isCoarsestSievingPortion = namePrefix!.endsWith('WholePortion.Separation.CoarsePortion');
  const isFineSievingPortion = namePrefix!.endsWith('FinePortion.Subspecimen');
  const isIntermediatePortion = !isCoarsestSievingPortion && namePrefix!.endsWith('CoarsePortion');

  // Portion number for intermediate portions. Since we only use it for intermediate portions, and
  // we support a maxmimum of 3 separations, the only case that we actually end up using it for is
  // the coarse portion of the third separation, and we expect the value to be 3 for this case.
  const intermediatePortionNumber = isIntermediatePortion ? namePrefix?.match(/\.Separation\./g)?.length ?? 0 : 0;

  const heading = isWholePortion
    ? 'Portion Sieving'
    : isCoarsestSievingPortion
    ? 'Coarse Portion Sieving'
    : isFineSievingPortion
    ? 'Fine Portion Sieving'
    : `Portion ${intermediatePortionNumber} Sieving`;

  const parentPath = namePrefix?.substr(0, namePrefix?.lastIndexOf('.'));

  const isFinePortionDry = useSelector(
    pipe(
      createWorksheetSelector!(`${parentPath}.FinePortion.Preparation.StateAfterInitialPreparation`, false),
      (s: StateOfMaterial) => s === StateOfMaterial.OvenDry
    )
  );

  const isCoarsePortionOfSeparationDuringSieving = isCoarsestSievingPortion || isIntermediatePortion;

  return (
    <PageSection heading={heading}>
      <PortionSievingDetailsSection />
      <NamePrefixModifier appendNamePrefix="Sieving">
        <PortionSievingEquipmentSection />
        <PortionSievingRecordGridSection
          isCoarsePortionOfSeparationDuringSieving={isCoarsePortionOfSeparationDuringSieving}
        />
      </NamePrefixModifier>
      {isCoarsePortionOfSeparationDuringSieving && (
        <ListFieldSection>
          <NamePrefixModifier namePrefix={parentPath}>
            <Field
              input={<TextOutput />}
              label={`Expected Mass Passing`}
              name={`FinePortion.Masses.${isFinePortionDry ? 'DryMass' : 'WetMass'}`}
            />
          </NamePrefixModifier>
        </ListFieldSection>
      )}
    </PageSection>
  );
};

PortionSievingPageSection = withInjectedProps(PortionSievingPageSection);

export { PortionSievingPageSection };
export default PortionSievingPageSection;
